// useWorkflowMap.js — Workflow Map feature hook
//
// ── Responsibilities ──────────────────────────────────────────────────────────
//
//  1. Accept workflow filter values from rtlsFilter.js "workflow" mode
//     (via rtlsMap.js handleRtlsFilterApply → case "workflow").
//  2. Call fetchWorkflowMap() (dataReceiver.js → APIM → dataServe.js) to get:
//       - Job execution time windows from SQL
//       - Position history pre-filtered to those windows
//  3. Bin the raw [x, y, z] positions into a 1 m² grid client-side.
//  4. Draw Cylinder heatmap shapes on the live Riot.Map instance
//     (colour + radius scaled by bin hit-count, exactly as useHeatmap.js does).
//  5. Draw Text labels on high-density bins when showLabels=true.
//  6. Expose job windows data for timeline / tooltip rendering in the parent.
//  7. Clean up all drawn shapes on clear() or re-apply.
//
// ── Data flow ─────────────────────────────────────────────────────────────────
//
//  rtlsFilter.js
//    → rtlsMap.js handleRtlsFilterApply (case "workflow")
//      → useWorkflowMap.apply({ objectName, jobName, jobNumber, beginTs, endTs })
//          → fetchWorkflowMap()   [dataReceiver → APIM → dataServe]
//             backend resolves object_id, queries SQL, fetches & filters positions
//          → binPositions()       [client-side 1 m² grid accumulation]
//          → draw Riot.Map.Cylinder per bin
//
// ── Coordinate system ─────────────────────────────────────────────────────────
//
//  The /position-history endpoint returns x/y in world-space metres (same
//  system as /heat-map cell_x / cell_y).  No conversion is needed; values go
//  straight into Riot.Map shape constructors after binning.
//
// ── Differences from useHeatmap ──────────────────────────────────────────────
//
//  - useHeatmap  uses the /heat-map backend endpoint which pre-bins cells.
//  - useWorkflowMap  receives raw [x, y, z] position points filtered to job
//    windows; it bins them client-side in binPositions() so both hooks end up
//    with the same [cellX, cellY, count] array before drawing.
//
//  This design keeps the backend handler simple (no grid logic server-side)
//  and makes cell-size tuning a pure front-end concern.

import { useState, useCallback, useRef } from "react";
import { fetchWorkflowMap, DataReceiverError } from "../../utils/dataReceiver";

// ─── Grid constants ───────────────────────────────────────────────────────────
//
// CELL_SIZE  — width / height of each bin in world-space metres.
//   1 m gives enough spatial resolution for a factory floor without producing
//   so many bins that the map becomes unreadably dense.  Increase to 2 m for
//   wider facilities; decrease to 0.5 m for fine-grain analysis.

const CELL_SIZE = 1.0;

// ─── Shape constants (mirror useHeatmap.js for visual consistency) ────────────

const DISC_Z_BOTTOM = 0; // floor plane
const DISC_Z_TOP = 0.1; // minimal height so Cylinder is never zero-volume
const LABEL_Z = 0.6; // label floats just above the disc top
const LABEL_THRESHOLD = 0.6; // normalised density above which labels render
const RADIUS_MIN = 0.5; // metres — sparse bins
const RADIUS_MAX = 2.5; // metres — dense bins
const ALPHA_MIN = 0.2;
const ALPHA_MAX = 0.78;

// ─── Colour mapping (identical to useHeatmap.js) ──────────────────────────────
//
// Maps a normalised density value (0–1) to RGB via a 5-stop gradient:
//   blue(0) → cyan(0.25) → green(0.5) → yellow(0.75) → red(1)

function heatColor(t) {
  const stops = [
    [0, 0, 255], // t = 0.00  blue
    [0, 255, 255], // t = 0.25  cyan
    [0, 255, 0], // t = 0.50  green
    [255, 255, 0], // t = 0.75  yellow
    [255, 0, 0], // t = 1.00  red
  ];
  const scaled = t * (stops.length - 1);
  const lo = Math.floor(scaled);
  const hi = Math.min(lo + 1, stops.length - 1);
  const frac = scaled - lo;
  return stops[lo].map((v, i) => Math.round(v + (stops[hi][i] - v) * frac));
}

// CSS-ready version of the same 5 stops above (identical to useHeatmap.js),
// exported so a legend can render a gradient bar that always matches the
// actual disc colors.
export const HEAT_GRADIENT_STOPS_CSS = [
  "rgb(0, 0, 255)", // fewest positions
  "rgb(0, 255, 255)",
  "rgb(0, 255, 0)",
  "rgb(255, 255, 0)",
  "rgb(255, 0, 0)", // most positions
];
export const HEAT_GRADIENT_CSS = `linear-gradient(to right, ${HEAT_GRADIENT_STOPS_CSS.join(", ")})`;

// ─── Shape removal helper (mirrors useHeatmap.js) ────────────────────────────

function removeShape(map, shape) {
  try {
    shape.delete();
    return;
  } catch (_) {}
  try {
    if (map && typeof map.remove === "function") map.remove(shape);
  } catch (_) {}
}

// ─── Client-side position binning ─────────────────────────────────────────────
//
// Groups raw [x, y, z] position points into CELL_SIZE × CELL_SIZE grid cells.
// Returns an array of [cellCentreX, cellCentreY, hitCount] rows — the same
// shape that /heat-map returns from the backend, so the drawing loop below
// works identically for both hooks.
//
// Cell centre is at the geometric centre of the bin (floor-snap + half-cell).
// z is ignored for binning; the Cylinder is always drawn at floor level.
//
// @param {Array<[number, number, number]>} positions  [x, y, z] raw points
// @returns {Array<[number, number, number]>}           [cx, cy, count]

function binPositions(positions) {
  if (!positions || positions.length === 0) return [];

  const counts = new Map();

  for (const [x, y] of positions) {
    // Snap to cell centre: floor to grid then offset by half a cell
    const cx = Math.floor(x / CELL_SIZE) * CELL_SIZE + CELL_SIZE / 2;
    const cy = Math.floor(y / CELL_SIZE) * CELL_SIZE + CELL_SIZE / 2;
    const key = `${cx},${cy}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return Array.from(counts.entries()).map(([key, count]) => {
    const [cx, cy] = key.split(",").map(Number);
    return [cx, cy, count];
  });
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useWorkflowMap
 *
 * Full lifecycle: filter input → APIM fetch (SQL windows + position history)
 * → client-side binning → Riot.Map Cylinder heatmap → cleanup.
 *
 * @param {React.RefObject} riotMapRef  ref to the live Riot.Map instance
 *
 * @returns {{
 *   isActive:      boolean,
 *   isLoading:     boolean,
 *   error:         string | null,
 *   positionCount: number,          raw position points from the backend
 *   binCount:      number,          distinct grid cells drawn on the map
 *   windowCount:   number,          job execution windows found in SQL
 *   windows:       Array<{          expose for timeline / tooltip rendering
 *     start: number,
 *     end:   number,
 *     raw:   object,
 *   }>,
 *   path:          "module" | "personnel" | null,
 *   rtlsObjectId:  number | null,
 *   minCount:      number,
 *   maxCount:      number,
 *   apply:         (filters, options?) => Promise<void>,
 *   clear:         () => void,
 * }}
 *
 * filters (from rtlsFilter.js onApply via rtlsMap.js):
 *   {
 *     objectName: string,
 *     jobName:    string,
 *     jobNumber?: number | null,
 *     beginTs:    number,    Unix seconds
 *     endTs:      number,    Unix seconds
 *   }
 *
 * options:
 *   {
 *     showLabels?:     boolean,  default false  — count labels on dense bins
 *     samplingPeriod?: number,   default 30     — seconds between position samples
 *   }
 */
export function useWorkflowMap(riotMapRef) {
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [positionCount, setPositionCount] = useState(0);
  const [binCount, setBinCount] = useState(0);
  const [windowCount, setWindowCount] = useState(0);
  const [windows, setWindows] = useState([]);
  const [path, setPath] = useState(null);
  const [rtlsObjectId, setRtlsObjectId] = useState(null);
  // Density bounds behind the currently-drawn colors — the gradient is
  // normalised per-render (min→max of THIS bin set), so a legend needs
  // these to say what blue/red actually mean right now.
  const [minCount, setMinCount] = useState(0);
  const [maxCount, setMaxCount] = useState(0);

  // Tracks all shapes drawn so clear() can remove every one
  const shapesRef = useRef([]);
  // AbortController for the in-flight APIM fetch — cancelled on clear/re-apply
  const abortRef = useRef(null);

  // ── clear ───────────────────────────────────────────────────────────────────

  const clear = useCallback(
    function () {
      // Cancel any in-flight request
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }

      const map = riotMapRef.current;
      const shapes = shapesRef.current;
      console.log(
        "[useWorkflowMap] clear() — removing " + shapes.length + " shapes",
      );
      shapes.forEach((shape) => removeShape(map, shape));
      shapesRef.current = [];

      setIsActive(false);
      setPositionCount(0);
      setBinCount(0);
      setWindowCount(0);
      setWindows([]);
      setPath(null);
      setRtlsObjectId(null);
      setMinCount(0);
      setMaxCount(0);
      setError(null);
    },
    [riotMapRef],
  );

  // ── apply ───────────────────────────────────────────────────────────────────

  const apply = useCallback(
    function (filters, options) {
      if (!filters) filters = {};
      if (!options) options = {};

      // Validate map is ready BEFORE clearing anything so a mis-fire doesn't
      // wipe an existing heatmap for no reason.
      const map = riotMapRef.current;
      if (!map) {
        console.warn("[useWorkflowMap] apply: map not ready");
        return Promise.resolve();
      }

      const Riot = window.Riot;
      if (!Riot?.Map?.Cylinder) {
        setError(
          "RTLS SDK not ready — wait for the map to connect before applying a workflow map.",
        );
        return Promise.resolve();
      }

      // ── Destructure filters from rtlsFilter.js onApply (workflow branch) ───
      // Shape: { objectName, jobName, jobNumber?, beginTs, endTs, beginDateTime, endDateTime }
      //
      // beginTs/endTs (Unix seconds) — used by the position-history API call
      //   inside the backend; kept here for logging only.
      // beginDateTime/endDateTime ("YYYY-MM-DDTHH:MM") — used by SQL WHERE
      //   clauses so filtering respects the exact hour/minute the user picked.
      const {
        objectName,
        jobName,
        jobNumber = null,
        beginTs,
        endTs,
        beginDateTime,
        endDateTime,
      } = filters;
      const showLabels = options.showLabels ?? false;
      const samplingPeriod = options.samplingPeriod ?? 30;

      if (!objectName || !jobName) {
        setError("An object name and job selection are required.");
        return Promise.resolve();
      }
      if (!beginTs || !endTs) {
        setError("A date range is required to generate a workflow map.");
        return Promise.resolve();
      }
      if (beginTs >= endTs) {
        setError("'From' date must be earlier than 'To' date.");
        return Promise.resolve();
      }
      // beginDateTime/endDateTime are required by the SQL WHERE clauses.
      // Access points #2 and #3 (metrics pages) must derive these from their
      // timestamps using: new Date(ts * 1000).toISOString().slice(0, 16)
      if (!beginDateTime || !endDateTime) {
        setError(
          "beginDateTime and endDateTime are required for date filtering. " +
            "Ensure the filter payload includes both fields.",
        );
        return Promise.resolve();
      }

      setIsLoading(true);
      setError(null);
      // Clear existing shapes before async work begins (mirrors useHeatmap.js)
      clear();

      // Fresh AbortController for this fetch lifecycle
      const abortController = new AbortController();
      abortRef.current = abortController;

      console.log("[useWorkflowMap] Fetching workflow map via APIM…", {
        objectName,
        jobName,
        jobNumber,
        beginTs,
        endTs,
        beginDateTime,
        endDateTime,
        samplingPeriod,
      });

      // ── Step 1: fetch via dataReceiver → APIM → dataServe ─────────────────
      //
      // The backend handles:
      //   - Module vs. personnel path detection (12WO prefix)
      //   - SQL job window lookup filtered to [beginDateTime, endDateTime]
      //   - RTLS object_id resolution
      //   - position-history fetch + window filtering
      //
      // We receive pre-filtered raw [x, y, z] points; binning happens below.

      return fetchWorkflowMap(
        {
          objectName,
          jobName,
          jobNumber,
          beginTs,
          endTs,
          beginDateTime,
          endDateTime,
          samplingPeriod,
        },
        abortController.signal,
      )
        .then(function (data) {
          // Guard: abort may have fired during the fetch
          if (abortController.signal.aborted) return;

          console.log("[useWorkflowMap] Response received", {
            path: data.path,
            rtlsObjectId: data.rtlsObjectId,
            windowCount: data.windowCount,
            positionCount: data.positionCount,
            noDataReason: data.noDataReason ?? null,
            rawPositionCount: data.meta?.rawPositionCount,
          });

          // Persist metadata for the parent even before we know if positions exist
          setPath(data.path);
          setRtlsObjectId(data.rtlsObjectId ?? null);
          setWindows(Array.isArray(data.windows) ? data.windows : []);
          setWindowCount(data.windowCount ?? 0);

          // ── Step 2: surface meaningful empty-state errors ─────────────────
          //
          // Three distinct failure modes — each needs its own message:
          //   a) object not found in Kinexon → badge / tag issue
          //   b) no SQL rows in range        → job wasn't run in this period
          //   c) SQL rows exist but all had null end + null ReportedHours
          //                                  → in-progress with no duration info
          //   d) no positions inside windows  → tracker inactive during job

          if (!data.meta?.objectResolved) {
            setError(
              `Object "${objectName}" was not found in the RTLS system. ` +
                "Verify the name matches the live objects list exactly.",
            );
            setIsLoading(false);
            return;
          }

          if (data.windowCount === 0) {
            // Use noDataReason from the backend to pick the right message.
            // "no_valid_windows" means SQL rows were found but every one had
            // RealizedEndDate IS NULL AND ReportedHours IS NULL — the job is
            // in progress but no duration information has been recorded yet.
            const reason = data.noDataReason ?? "no_sql_rows";
            setError(
              reason === "no_valid_windows"
                ? `"${jobName}" was found for "${objectName}" but the job ` +
                    "is still in progress and has no reported hours yet. " +
                    "Try again once the job has been completed or hours have been logged."
                : `No "${jobName}" activity was recorded for "${objectName}" ` +
                    "in the selected date range. Try widening the range.",
            );
            setIsLoading(false);
            return;
          }

          if (data.positionCount === 0) {
            setError(
              `Found ${data.windowCount} job window${data.windowCount !== 1 ? "s" : ""} ` +
                `but no position data for "${objectName}" during those times. ` +
                "The tracker may have been inactive.",
            );
            setIsLoading(false);
            return;
          }

          setPositionCount(data.positionCount);

          // ── Step 3: bin raw positions into a heatmap grid ─────────────────
          //
          // binPositions() accumulates each [x, y] point into its
          // CELL_SIZE × CELL_SIZE cell.  The result has the same
          // [cellX, cellY, count] shape as the /heat-map endpoint so the
          // drawing code below is identical to useHeatmap.js.

          const cells = binPositions(data.positions);

          if (cells.length === 0) {
            // Shouldn't happen if positionCount > 0, but guard defensively
            setError("Position data could not be mapped to grid cells.");
            setIsLoading(false);
            return;
          }

          console.log(
            `[useWorkflowMap] Binned ${data.positionCount} points → ${cells.length} cells`,
          );

          // ── Step 4: normalise counts for colour and radius scaling ─────────

          const counts = cells.map((c) => c[2]);
          const maxCount = Math.max(...counts);
          const minCount = Math.min(...counts);
          const range = maxCount - minCount || 1;

          console.log(
            `[useWorkflowMap] Drawing ${cells.length} cells. ` +
              `Count range: ${minCount}–${maxCount}`,
          );

          // Re-read map ref AFTER the async fetch (mirrors useHeatmap.js guard)
          const currentMap = riotMapRef.current;
          if (!currentMap) {
            console.warn(
              "[useWorkflowMap] map became unavailable during fetch — aborting draw",
            );
            setIsLoading(false);
            return;
          }

          const currentRiot = window.Riot;
          if (!currentRiot?.Map?.Cylinder) {
            setError("RTLS SDK became unavailable during fetch.");
            setIsLoading(false);
            return;
          }

          // ── Step 5: draw one Cylinder disc per cell ────────────────────────
          //
          // Coordinate system note:
          //   binPositions() preserves the world-space metre coordinates from
          //   the Kinexon API.  Cell centres are passed directly to shape
          //   constructors — no extra origin offset is needed.
          //
          // Cylinder API (jsapi.md):
          //   center: [x, y, z]   ← 3-element array required
          //   radius: number
          //   top:    number      ← must differ from bottom to avoid zero volume
          //   bottom: number
          //   color:  [r, g, b, a]

          const newShapes = [];

          cells.forEach(function ([cx, cy, count]) {
            const t = (count - minCount) / range;
            const [r, g, b] = heatColor(t);
            const alpha = ALPHA_MIN + t * (ALPHA_MAX - ALPHA_MIN);
            const radius = RADIUS_MIN + t * (RADIUS_MAX - RADIUS_MIN);

            try {
              const disc = new currentRiot.Map.Cylinder({
                center: [cx, cy, DISC_Z_BOTTOM],
                radius,
                top: DISC_Z_TOP,
                bottom: DISC_Z_BOTTOM,
                color: [r, g, b, alpha],
              });
              currentMap.add(disc);
              newShapes.push(disc);
            } catch (shapeErr) {
              console.warn(
                `[useWorkflowMap] Failed to draw disc at (${cx}, ${cy}):`,
                shapeErr,
              );
            }

            // Optional count label on high-density bins
            if (showLabels && t >= LABEL_THRESHOLD && currentRiot.Map.Text) {
              try {
                const lbl = new currentRiot.Map.Text({
                  text: String(count),
                  position: [cx, cy, LABEL_Z],
                  size: 11,
                  color: [255, 255, 255, 0.9],
                  align: "center",
                });
                currentMap.add(lbl);
                newShapes.push(lbl);
              } catch (_) {
                // Label failure is non-critical — disc is already drawn
              }
            }
          });

          // ── Step 6: persist refs and update state ──────────────────────────
          shapesRef.current = newShapes;
          setIsActive(true);
          setBinCount(cells.length);
          setMinCount(minCount);
          setMaxCount(maxCount);

          console.log(
            `[useWorkflowMap] Done — ${newShapes.length} shapes drawn ` +
              `(${cells.length} cells from ${data.positionCount} position points, ` +
              `${data.windowCount} job window${data.windowCount !== 1 ? "s" : ""}).`,
          );
        })
        .catch(function (err) {
          // Ignore AbortError — user cleared / re-applied mid-fetch
          if (err.name === "AbortError") return;

          console.error("[useWorkflowMap] apply error:", err);
          setError(
            err instanceof DataReceiverError
              ? err.message
              : err.message || "Failed to load workflow map data.",
          );
          clear();
        })
        .finally(function () {
          // Only clear loading state if this controller is still the active one.
          // If clear() fired mid-fetch it will have already nulled abortRef and
          // reset state; we must not clobber that with a stale finally.
          if (abortRef.current === abortController) {
            setIsLoading(false);
            abortRef.current = null;
          }
        });
    },
    [riotMapRef, clear],
  );

  return {
    isActive,
    isLoading,
    error,
    positionCount,
    binCount,
    windowCount,
    windows,
    path,
    rtlsObjectId,
    minCount,
    maxCount,
    apply,
    clear,
  };
}
